import React from 'react';
import { MarketSignalEvent } from '@/types/signals';
import { formatTime } from '@/app/lib/utils';
import { useTranslations } from 'next-intl';

import Pill from '../ui/Pill';
import { getAttributionPresentation, getSignalAttribution } from './attributionPresentation';
import { SIGNAL_ACTION_TRANSLATION_KEYS } from './signalFilterConfigs';

type MarketSignalActivityItemProps = {
  signal: MarketSignalEvent;
};

const MarketSignalActivityItem = React.memo(({ signal }: MarketSignalActivityItemProps) => {
  const t = useTranslations('signals');
  const topic = signal.market.topic;
  const action = signal.action || signal.market.trigger || '';
  const actionTranslationKey = SIGNAL_ACTION_TRANSLATION_KEYS[action];
  const actionLabel = actionTranslationKey ? t(actionTranslationKey as never) : action;
  const activityAt = signal.signal_at || signal.captured_at || signal.created_at;
  const attribution = getSignalAttribution(signal);
  const attributionPresentation = getAttributionPresentation(attribution);

  return (
    <div className="flex flex-col gap-2 border-b border-border p-3 text-[11px] last:border-b-0">
      <div className="flex min-w-0 items-center gap-1">
        <span className="shrink-0 text-muted-foreground">{formatTime(activityAt)} -</span>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <span className="shrink-0 text-muted-foreground">{actionLabel}</span>
          <span
            className="min-w-0 flex-1 truncate font-medium text-secondary-foreground"
            title={topic}
          >
            {topic}
          </span>
        </div>
      </div>
      {attributionPresentation?.sourceName && (
        <div className="flex items-center">
          <Pill variant="outline" className="gap-1.5 px-2">
            {attributionPresentation.sourceLogo && (
              <img
                src={attributionPresentation.sourceLogo}
                alt=""
                className="size-3.5 object-contain"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span>{attributionPresentation.sourceName}</span>
          </Pill>
        </div>
      )}
    </div>
  );
});

MarketSignalActivityItem.displayName = 'MarketSignalActivityItem';

export default MarketSignalActivityItem;
